/**
 * useOnboarding - Onboarding status and complete-step / complete / skip-mfa.
 * MODULE: core/auth/frontend/hooks/useOnboarding.ts
 */
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { OnboardingStatus } from '../../contracts/auth.types';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;
const getToken = () => localStorage.getItem('access_token');

async function fetchStatus(): Promise<OnboardingStatus> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/onboarding/status`, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!res.ok) throw new Error('Failed to fetch onboarding status');
  const data = await res.json();
  return {
    completed: data.completed,
    currentStep: data.current_step,
    steps: {
      accountCreated: data.steps?.account_created ?? false,
      passwordSet: data.steps?.password_set ?? false,
      tenantSelected: data.steps?.tenant_selected ?? false,
      targetsSet: data.steps?.targets_set ?? false,
      mfaConfigured: data.steps?.mfa_configured ?? false,
      profileCompleted: data.steps?.profile_completed ?? false,
    },
  };
}

async function completeStep(step: string, data?: Record<string, unknown>): Promise<OnboardingStatus> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/onboarding/complete-step`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ step, data }),
  });
  if (!res.ok) throw new Error('Failed to complete step');
  const d = await res.json();
  return {
    completed: d.completed,
    currentStep: d.current_step,
    steps: {
      accountCreated: d.steps?.account_created ?? false,
      passwordSet: d.steps?.password_set ?? false,
      tenantSelected: d.steps?.tenant_selected ?? false,
      targetsSet: d.steps?.targets_set ?? false,
      mfaConfigured: d.steps?.mfa_configured ?? false,
      profileCompleted: d.steps?.profile_completed ?? false,
    },
  };
}

async function skipMfa(): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/onboarding/skip-mfa`, {
    method: 'POST',
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!res.ok) throw new Error('Failed to skip MFA');
}

async function completeOnboarding(): Promise<{ redirectUrl: string }> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/onboarding/complete`, {
    method: 'POST',
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!res.ok) throw new Error('Failed to complete onboarding');
  const data = await res.json();
  return { redirectUrl: data.redirect_url || '/dashboard' };
}

export function useOnboarding() {
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ['onboarding'],
    queryFn: fetchStatus,
    enabled: !!getToken(),
  });
  const completeStepMut = useMutation({
    mutationFn: ({ step, data }: { step: string; data?: Record<string, unknown> }) =>
      completeStep(step, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
  const skipMfaMut = useMutation({
    mutationFn: skipMfa,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
  const completeMut = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
  const steps = status?.steps ?? {};
  const progress =
    Object.keys(steps).length > 0
      ? (Object.values(steps).filter(Boolean).length / Object.keys(steps).length) * 100
      : 0;
  return {
    status: status ?? null,
    isLoading,
    completeStep: (step: string, data?: Record<string, unknown>) =>
      completeStepMut.mutateAsync({ step, data }),
    skipMfa: () => skipMfaMut.mutateAsync(),
    completeOnboarding: () => completeMut.mutateAsync(),
    currentStep: status?.currentStep ?? 'pending',
    progress,
  };
}
